import React from 'react';
import {
    Box,
    Paper,
    Typography,
    IconButton,
} from '@mui/material';
import { formatExternalUrl } from '../../utils/formatUrl';
import {
    LinkedIn as LinkedInIcon,
    YouTube as YouTubeIcon,
    Language as WebsiteIcon,
    OpenInNew as ExternalLinkIcon,
    Add as AddIcon,
    RemoveCircle as RemoveCircleIcon,
    Facebook as FacebookIcon,
    Instagram as InstagramIcon,
    Twitter as TwitterIcon,
    RateReview as YelpIcon,
    Map as GoogleMapsIcon,
    Navigation as MapquestIcon,
    Business as YellowPagesIcon,
    Web as WebPresenceIcon
} from '@mui/icons-material';
import SourceButton from '../PhoneTab/SourceButton.js';
import { Colors } from '../../styles/colors';

const StatusItem = ({ type, text }) => {
    const isWarning = type === 'warning';
    const isSuccess = type === 'success';
    const isNeutral = type === 'neutral';

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            {isWarning && (
                <Box sx={{
                    backgroundColor: Colors.ERROR_RED,
                    borderRadius: '50%',
                    width: 18,
                    height: 18,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                }}>
                    <Typography sx={{ color: 'white', fontSize: '12px', fontWeight: 'bold' }}>!</Typography>
                </Box>
            )}
            {isSuccess && (
                <Box sx={{
                    backgroundColor: Colors.SUCCESS_GREEN,
                    borderRadius: '50%',
                    width: 18,
                    height: 18,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                }}>
                    <AddIcon sx={{ fontSize: 12, color: 'white', fontWeight: 'bold' }} />
                </Box>
            )}
            {isNeutral && (
                <RemoveCircleIcon sx={{ fontSize: 18, color: '#9CA3AF', flexShrink: 0 }} />
            )}
            <Typography variant="body2" sx={{
                color: '#374151',
                fontSize: '0.8rem',
                fontWeight: 400,
                lineHeight: 1.3
            }}>
                {text}
            </Typography>
        </Box>
    );
};

const getPlatformConfig = (platform) => {
    const platformLower = platform?.toLowerCase();

    if (platformLower?.includes('linkedin')) {
        return {
            icon: <LinkedInIcon sx={{ color: 'white', fontSize: 20 }} />,
            backgroundColor: '#F8FCFF',
            iconBg: '#0077B5'
        };
    }
    if (platformLower?.includes('youtube')) {
        return {
            icon: <YouTubeIcon sx={{ color: 'white', fontSize: 20 }} />,
            backgroundColor: '#FFFAFA',
            iconBg: '#FF0000'
        };
    }
    if (platformLower?.includes('facebook') || platformLower?.includes('fb')) {
        return {
            icon: <FacebookIcon sx={{ color: 'white', fontSize: 20 }} />,
            backgroundColor: '#FAFCFF',
            iconBg: '#1877F2'
        };
    }
    if (platformLower?.includes('instagram') || platformLower?.includes('ig')) {
        return {
            icon: <InstagramIcon sx={{ color: 'white', fontSize: 20 }} />,
            backgroundColor: '#FFFBFD',
            iconBg: '#E4405F'
        };
    }
    if (platformLower?.includes('twitter') || platformLower?.includes('x.com') || platformLower === 'x') {
        return {
            icon: <TwitterIcon sx={{ color: 'white', fontSize: 20 }} />,
            backgroundColor: '#FAFCFF',
            iconBg: '#1DA1F2'
        };
    }
    if (platformLower?.includes('yelp')) {
        return {
            icon: <YelpIcon sx={{ color: 'white', fontSize: 20 }} />,
            backgroundColor: '#FFFCFC',
            iconBg: '#FF1A1A'
        };
    }
    if (platformLower?.includes('google') && platformLower?.includes('map')) {
        return {
            icon: <GoogleMapsIcon sx={{ color: 'white', fontSize: 20 }} />,
            backgroundColor: '#FBFCFB',
            iconBg: '#34A853'
        };
    }
    if (platformLower?.includes('mapquest')) {
        return {
            icon: <MapquestIcon sx={{ color: 'white', fontSize: 20 }} />,
            backgroundColor: '#FFFEF8',
            iconBg: '#F59E0B'
        };
    }
    if (platformLower?.includes('yellow') && platformLower?.includes('pages')) {
        return {
            icon: <YellowPagesIcon sx={{ color: 'white', fontSize: 20 }} />,
            backgroundColor: '#FFFFFA',
            iconBg: '#EAB308'
        };
    }
    if (platformLower?.includes('domain') || platformLower?.includes('website') || platformLower?.includes('site')) {
        return {
            icon: <WebsiteIcon sx={{ color: 'white', fontSize: 20 }} />,
            backgroundColor: '#FDFDFE',
            iconBg: '#6B7280'
        };
    }

    return {
        icon: <WebsiteIcon sx={{ color: 'white', fontSize: 20 }} />,
        backgroundColor: '#FEFEFE',
        iconBg: '#1E293B'
    };
};

const WebPresenceCard = ({
    title = "Web Presence",
    platform = "website",
    url,
    source = [],
    assessments = [],
    width = 360
}) => {
    const platformConfig = getPlatformConfig(platform);

    const handleExternalLinkClick = (e) => {
        e.stopPropagation();
        if (url) {
            window.open(formatExternalUrl(url), '_blank', 'noopener,noreferrer');
        }
    };

    // Sort and dedupe assessments like in AddressCard
    const isFollowerAssessment = (a) =>
        (a.name && /FollowerCount/i.test(a.name)) ||
        (a.ui_string && /follower/i.test(a.ui_string));

    const sortedAssessments = assessments
        .slice()
        .sort((a, b) => {
            const aFollower = isFollowerAssessment(a) ? 0 : 1;
            const bFollower = isFollowerAssessment(b) ? 0 : 1;
            if (aFollower !== bFollower) return aFollower - bFollower;

            const getTypeOrder = (assessment) => {
                switch(assessment.result_sign) {
                    case 'Positive': return 0; // warning first
                    case 'Negative': return 1; // success second
                    default: return 2; // neutral last
                }
            };
            return getTypeOrder(a) - getTypeOrder(b);
        })
        .filter((item, index, self) =>
            index === self.findIndex((t) => t.ui_string === item.ui_string)
        );

    return (
        <Paper
            onClick={handleExternalLinkClick}
            sx={{
                p: 2,
                borderRadius: 2,
                backgroundColor: platformConfig.backgroundColor,
                position: 'relative',
                width: width,
                minHeight: 200,
                maxWidth: '100%',
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
                cursor: url ? 'pointer' : 'default',
                transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                '&:hover': url ? {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                } : {}
            }}
        >
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{
                        backgroundColor: platformConfig.iconBg,
                        borderRadius: '6px',
                        p: 0.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        '& svg': {
                            fontSize: '24px'
                        }
                    }}>
                        {platformConfig.icon}
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#1E293B', fontSize: '1rem' }}>
                        {title}
                    </Typography>
                </Box>
                <IconButton
                    onClick={handleExternalLinkClick}
                    sx={{
                        backgroundColor: 'white',
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                        '&:hover': {
                            backgroundColor: '#F8FAFC',
                        }
                    }}
                    size="small"
                >
                    <ExternalLinkIcon sx={{ fontSize: 18 }} />
                </IconButton>
            </Box>

            {/* URL Display */}
            <Typography
                variant="body2"
                sx={{
                    color: '#4B5563',
                    fontSize: '0.875rem',
                    mb: 2,
                    wordBreak: 'break-all',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: '100%',
                    fontWeight: 400,
                    lineHeight: 1.4,
                }}
            >
                {url || 'No URL available'}
            </Typography>

            {/* Assessments */}
            <Box sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: 0,
                maxHeight: '140px',
                overflowY: 'auto',
                mb: 2
            }}>
                {sortedAssessments
                    .filter((assessment) => {
                        // Filter out assessments with name "EntitySocialMediaPresence"
                        return assessment.name !== 'EntitySocialMediaPresence';
                    })
                    .map((assessment, index) => {
                        let type;
                        switch(assessment.result_sign) {
                            case 'Negative':
                                type = 'success';
                                break;
                            case 'Positive':
                            type = 'warning';
                            break;
                        default:
                            type = 'neutral';
                    }

                    return (
                        <StatusItem
                            key={index}
                            type={type}
                            text={assessment.ui_string}
                        />
                    );
                })}
            </Box>

            {/* Source Button */}
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mt: 'auto'
            }} onClick={(e) => e.stopPropagation()}>
                {sortedAssessments.length > 5 && (
                    <Typography
                        variant="body2"
                        sx={{
                            color: '#6B7280',
                            fontSize: '0.75rem',
                        }}
                    >
                        {sortedAssessments.length} items
                    </Typography>
                )}
                <Box sx={{ ml: 'auto' }}>
                    <SourceButton sources={source} />
                </Box>
            </Box>
        </Paper>
    );
};

export default WebPresenceCard;
